import type { CSSProperties } from "react";
import { NavLink } from "react-router-dom";
import type { StudentActiveCohort } from "../auth/api-client";
import { resolveCourseHeroImageUrl } from "../media/courseHeroImage";
import { courseScrimColor } from "../teacher/courseScrim";
import { textColorsForBackground } from "../teacher/lessonCardColors";

const DEFAULT_CARD_BG = "linear-gradient(135deg, #1b4d2e, #2e7d52)";

type StudentSidebarCohortLinkProps = {
  readonly cohort: StudentActiveCohort;
  readonly collapsed: boolean;
};

export function StudentSidebarCohortLink({ cohort, collapsed }: StudentSidebarCohortLinkProps) {
  const accent = cohort.accentColor?.trim() || null;
  const heroImageUrl = resolveCourseHeroImageUrl(cohort);
  const hasImage = Boolean(heroImageUrl);
  const scrim = courseScrimColor(accent);
  const cardBg = accent || DEFAULT_CARD_BG;
  const titleColors = accent && !hasImage ? textColorsForBackground(accent) : null;
  const tooltip = `${cohort.courseTitle} · ${cohort.label}`;

  if (collapsed) {
    return (
      <NavLink
        to={`/cohorts/${cohort.cohortId}`}
        className={({ isActive }) =>
          `student-sidebar-cohort-swatch${isActive ? " student-sidebar-cohort-swatch--active" : ""}`
        }
        title={tooltip}
        aria-label={tooltip}
        style={
          {
            "--cohort-thumb-scrim": scrim,
            ...(!hasImage ? { background: cardBg } : {}),
          } as CSSProperties
        }
        data-has-image={hasImage ? "" : undefined}
        data-has-accent={accent ? "" : undefined}
      >
        {heroImageUrl ? (
          <img className="student-sidebar-cohort-swatch-img" src={heroImageUrl} alt="" crossOrigin="anonymous" />
        ) : null}
        {hasImage ? <span className="student-sidebar-cohort-swatch-scrim" aria-hidden /> : null}
      </NavLink>
    );
  }

  return (
    <NavLink
      to={`/cohorts/${cohort.cohortId}`}
      className={({ isActive }) =>
        `student-sidebar-cohort-link${isActive ? " student-sidebar-cohort-link--active" : ""}`
      }
      title={tooltip}
    >
      <div
        className={[
          "student-sidebar-cohort-card",
          hasImage ? "student-sidebar-cohort-card--image" : null,
          accent ? "student-sidebar-cohort-card--accent" : null,
        ]
          .filter(Boolean)
          .join(" ")}
        style={
          {
            "--cohort-thumb-scrim": scrim,
            ...(accent && !hasImage ? { "--cohort-thumb-bg": accent } : {}),
          } as CSSProperties
        }
      >
        {heroImageUrl ? (
          <img
            className="student-sidebar-cohort-card-img"
            src={heroImageUrl}
            alt=""
            loading="lazy"
            crossOrigin="anonymous"
          />
        ) : null}
        {hasImage ? <div className="student-sidebar-cohort-card-scrim" aria-hidden /> : null}
        <span
          className="student-sidebar-cohort-card-title"
          style={titleColors ? { color: titleColors.title } : undefined}
        >
          {cohort.courseTitle}
        </span>
      </div>
      <span className="student-sidebar-cohort-label">{cohort.label}</span>
    </NavLink>
  );
}
